import React, { useState } from 'react';
import { Box, Typography, Button, Menu, MenuItem, Chip, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LanguageIcon from '@mui/icons-material/Language';
import CircleIcon from '@mui/icons-material/Circle';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LockIcon from '@mui/icons-material/Lock';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../config/api';
import versionInfo from '../config/version.json';
import OfflineQueueModal from './OfflineQueueModal';

export default function Header() {
    const { 
        activeView, 
        setActiveView, 
        currentRole, 
        setCurrentRole, 
        showToast, 
        sidebarMobileOpen, 
        setSidebarMobileOpen, 
        sidebarCollapsed, 
        setSidebarCollapsed,
        tenantSubdomain,
        logout,
        currentUser,
        isOnline,
        offlineOrdersQueue,
        syncOfflineOrders
    } = useApp();
    const { currentLanguage, switchLanguage, t } = useLanguage();
    const isMr = currentLanguage === 'mr';
    const [anchorEl, setAnchorEl] = useState(null);
    const [roleAnchorEl, setRoleAnchorEl] = useState(null);
    const [offlineQueueModalOpen, setOfflineQueueModalOpen] = useState(false);

    // Dialog states
    // Removed legacy role switcher states

    const getViewTitle = () => {
        switch (activeView) {
            case 'dashboard': return t('nav-dash');
            case 'tables': return t('nav-tables');
            case 'pos': return t('nav-pos');
            case 'kds': return t('nav-kds');
            case 'inventory': return t('nav-inventory');
            case 'captain': return t('nav-waiter-sim');
            case 'reports': return t('nav-reports');
            case 'settings': return t('nav-reports');
            case 'accounting': return t('nav-accounting');
            case 'subscriptions': return t('nav-subscriptions');
            case 'ownerSettings': return t('nav-owner-settings');
            case 'liveStatus': return t('nav-live-status');
            case 'tokenDisplay': return t('nav-token-display');
            case 'qrOrdering': return t('nav-qr-order');
            case 'crm': return t('nav-crm');
            case 'cloudSaaS': return t('nav-cloud-saas');
            case 'developerDashboard': return 'Developer Admin Portal';
            default: return 'Dinexa';
        }
    };

    const handleLangClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLangSelect = (lang) => {
        switchLanguage(lang);
        setAnchorEl(null);
    };

    const handleLangClose = () => {
        setAnchorEl(null);
    };

    const getLangLabel = () => {
        if (currentLanguage === 'mr') return 'मराठी';
        if (currentLanguage === 'hi') return 'हिंदी';
        return 'English';
    };

    // Role switcher Actions
    const handleRoleClick = (event) => {
        setRoleAnchorEl(event.currentTarget);
    };

    const handleRoleClose = () => {
        setRoleAnchorEl(null);
    };

    const getRoleLabel = () => {
        if (currentRole === 'owner') return t('role-owner');
        if (currentRole === 'manager') return t('role-manager');
        if (currentRole === 'developer') return 'Developer';
        return t('role-staff');
    };

    return (
        <Box sx={{
            height: 70,
            bgcolor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            px: { xs: 2, sm: 4 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
        }}>
            {/* Left Header section: Menu Toggles + View Title */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Universal Drawer Hamburger Icon (Mobile, Tablet, Desktop) */}
                <IconButton 
                    onClick={() => {
                        if (typeof setSidebarMobileOpen === 'function') {
                            setSidebarMobileOpen(prev => !prev);
                        }
                    }} 
                    sx={{ 
                        mr: 1.5, 
                        color: '#334155', 
                        bgcolor: '#f1f5f9', 
                        borderRadius: 2,
                        p: 0.9,
                        '&:hover': { bgcolor: '#e2e8f0', color: '#0f172a' } 
                    }}
                    aria-label="Toggle navigation drawer"
                >
                    <MenuIcon />
                </IconButton>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a', fontSize: { xs: '1rem', sm: '1.25rem', md: '1.4rem' } }}>
                        {getViewTitle()}
                    </Typography>
                    {currentUser && (currentUser.tenant_name || currentUser.branch_name) && currentRole !== 'developer' && (
                        <Chip
                            label={currentUser.is_multi_branch && currentUser.branch_name && currentUser.branch_name.toLowerCase() !== 'main branch' ? currentUser.branch_name : currentUser.tenant_name}
                            sx={{
                                bgcolor: '#008751',
                                color: '#ffffff',
                                fontWeight: 800,
                                fontSize: '0.8rem',
                                height: 26,
                                px: 0.5,
                                display: { xs: 'none', sm: 'inline-flex' }
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* Right Header Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 3 } }}>
                {/* Offline / Online Status Badge & Offline Queue Details Modal */}
                <Chip
                    icon={<CircleIcon sx={{ fontSize: '10px !important', color: (offlineOrdersQueue && offlineOrdersQueue.length > 0) ? '#f59e0b !important' : (isOnline ? '#10b981 !important' : '#ef4444 !important') }} />}
                    label={
                        offlineOrdersQueue && offlineOrdersQueue.length > 0
                            ? `⚡ ${offlineOrdersQueue.length} Queued (${isMr ? 'तपशील पहा' : 'View Details'})`
                            : (isOnline ? '🟢 Cloud Online' : '⚡ Offline Mode')
                    }
                    onClick={() => setOfflineQueueModalOpen(true)}
                    sx={{
                        bgcolor: (offlineOrdersQueue && offlineOrdersQueue.length > 0) ? '#fef3c7' : (isOnline ? '#ecfdf5' : '#fef2f2'),
                        color: (offlineOrdersQueue && offlineOrdersQueue.length > 0) ? '#b45309' : (isOnline ? '#047857' : '#991b1b'),
                        border: `1px solid ${(offlineOrdersQueue && offlineOrdersQueue.length > 0) ? '#fde68a' : (isOnline ? '#a7f3d0' : '#fecaca')}`,
                        fontWeight: 800,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        '&:hover': {
                            transform: 'scale(1.03)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        },
                        '& .MuiChip-icon': { ml: 1 },
                        display: 'inline-flex'
                    }}
                />

                {/* Language Selector */}
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<LanguageIcon />}
                        endIcon={<KeyboardArrowDownIcon />}
                        onClick={handleLangClick}
                        size="small"
                        sx={{
                            color: '#475569',
                            borderColor: '#cbd5e1',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            py: 0.6,
                            '&:hover': {
                                borderColor: '#94a3b8',
                                bgcolor: '#f8fafc'
                            }
                        }}
                    >
                        {getLangLabel()}
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleLangClose}
                        sx={{ mt: 1 }}
                    >
                        <MenuItem onClick={() => handleLangSelect('en')} selected={currentLanguage === 'en'}>English</MenuItem>
                        <MenuItem onClick={() => handleLangSelect('mr')} selected={currentLanguage === 'mr'}>मराठी</MenuItem>
                        <MenuItem onClick={() => handleLangSelect('hi')} selected={currentLanguage === 'hi'}>हिंदी</MenuItem>
                    </Menu>
                </Box>

                {/* Role switch profile toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button
                        onClick={handleRoleClick}
                        variant="text"
                        sx={{
                            p: 0.5,
                            textTransform: 'none',
                            color: '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            '&:hover': { bgcolor: '#f1f5f9' }
                        }}
                    >
                        <Avatar sx={{ 
                            bgcolor: currentRole === 'developer' ? '#6d28d9' : '#008751', 
                            width: 34, 
                            height: 34 
                        }}>
                            <AccountCircleIcon />
                        </Avatar>
                        <Box sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                                {currentRole === 'developer' ? 'Developer' : (currentUser ? currentUser.name : 'Store Owner')}
                            </Typography>
                            <Typography variant="caption" sx={{ 
                                color: currentRole === 'developer' ? '#7c3aed' : '#008751', 
                                fontWeight: 800, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 0.5 
                            }}>
                                <LockIcon sx={{ fontSize: 12 }} />
                                {currentRole === 'developer' ? 'Developer Login' : getRoleLabel()}
                            </Typography>
                        </Box>
                    </Button>
                    <Menu
                        anchorEl={roleAnchorEl}
                        open={Boolean(roleAnchorEl)}
                        onClose={handleRoleClose}
                        sx={{ mt: 1 }}
                    >
                        {currentRole !== 'developer' && [
                            <MenuItem 
                                key="check-updates" 
                                onClick={() => { 
                                    handleRoleClose(); 
                                    if (showToast) showToast(`🔄 v${versionInfo.version}.${versionInfo.buildNumber} (${versionInfo.buildTime}) ${t('check-updates')}...`); 
                                    if ('serviceWorker' in navigator) {
                                        navigator.serviceWorker.getRegistrations().then(registrations => {
                                            for (let registration of registrations) {
                                                registration.update();
                                            }
                                        });
                                    }
                                    setTimeout(() => {
                                        window.location.reload(true);
                                    }, 800);
                                }} 
                                sx={{ color: '#008751', fontWeight: 800 }}
                            >
                                🔄 {t('check-updates')}
                            </MenuItem>,
                            <Divider key="divider-2" sx={{ my: 0.5 }} />,
                            <MenuItem key="signout" onClick={() => { handleRoleClose(); logout(); }} sx={{ color: '#e11d48', fontWeight: 800 }}>
                                🚪 {t('sign-out')}
                            </MenuItem>
                        ]}
                        {currentRole === 'developer' && [
                            <MenuItem key="dev-info" disabled sx={{ opacity: 0.6, fontSize: '0.78rem', fontWeight: 700 }}>
                                🔐 Developer Admin Session
                            </MenuItem>,
                            <Divider key="dev-divider" sx={{ my: 0.5 }} />,
                            <MenuItem key="dev-exit" onClick={() => { handleRoleClose(); logout(); }} sx={{ color: '#7c3aed', fontWeight: 700 }}>
                                Exit Developer Mode
                            </MenuItem>
                        ]}
                    </Menu>
                </Box>
            </Box>

            {/* Offline Orders Queue Detailed Viewer Modal */}
            <OfflineQueueModal 
                open={offlineQueueModalOpen} 
                onClose={() => setOfflineQueueModalOpen(false)} 
            />
        </Box>
    );
}
